/** Create dialog for an API credential (react-hook-form + zod), with the
 *  one-time secret step.
 *
 *  The backend mints an opaque key and stores only its hash — the raw value
 *  exists in the 201 response and nowhere else, ever. So on success the
 *  dialog does NOT close: it switches to a secret step showing the key masked
 *  (reveal toggle + copy), with backdrop-close disabled and an explicit Done.
 *  The raw key lives in dialog state only — never in a query cache.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import * as apiCredentialsApi from '@/api/apiCredentials';
import type { ApiCredentialCreateScope } from '@/api/apiCredentials';
import { ConsoleApiError } from '@/api/client';
import { AppDialog } from '@/components/AppDialog';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { singlePageTenant } from '@/scope/pageTenants';

/** TenantPicker sentinel for a platform credential (no owning tenant). */
const SYSTEM_TENANT: TenantPickerValue = { id: 0, name: 'System (no tenant)' };

// No `partner_dev`: a partner's developer key is minted by its claim link only.
const SCOPE_CHOICES: { value: ApiCredentialCreateScope; label: string; hint: string }[] = [
  { value: 'tour_operator', label: 'Tour operator', hint: 'Partner/import API (bookings, tours, users).' },
  { value: 'webhooks', label: 'Webhooks', hint: 'Authenticates incoming webhook callbacks.' },
  { value: 'client', label: 'Client', hint: 'End-user client API.' },
  { value: 'admin', label: 'Admin', hint: 'Administrative API access.' },
];

const FormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(256, 'At most 256 characters'),
  scope: z.enum(['client', 'admin', 'tour_operator', 'webhooks']),
  description: z.string(),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = { name: '', scope: 'tour_operator', description: '' };

interface Props {
  open: boolean;
  /** The page's operators (D9): the one is pre-selected, all are featured; see
   *  `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
}

export function ApiCredentialCreateDialog({ open, pageTenants, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  // Tenant lives outside RHF (self-contained controlled picker); the pinned
  // "System" sentinel maps to tour_operator: null.
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [expires, setExpires] = useState<Date | null>(null);

  // The one-time secret step. rawKey is kept ONLY here.
  const [created, setCreated] = useState<{ id: number; name: string; rawKey: string } | null>(null);
  const [revealed, setRevealed] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(FormSchema), defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    reset(EMPTY);
    setTenant(singlePageTenant(pageTenants));
    setExpires(null);
    setCreated(null);
    setRevealed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: (body: apiCredentialsApi.ApiCredentialCreate) => apiCredentialsApi.create(body),
    onSuccess: (row) => {
      // Deliberately NOT invalidating the grid yet — that happens on Done, so
      // the secret step stays the single focus.
      setCreated({ id: row.id, name: row.name, rawKey: row.raw_key });
      setRevealed(false);
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Create failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    if (!tenant) {
      snackbar.show('Pick a tenant (or "System") first.', 'warning');
      return;
    }
    mutation.mutate({
      tour_operator: tenant.id === SYSTEM_TENANT.id ? null : tenant.id,
      name: v.name.trim(),
      scope: v.scope,
      ...(v.description.trim() ? { description: v.description.trim() } : {}),
      ...(expires ? { expires: expires.toISOString() } : {}),
    });
  });

  const finish = () => {
    void qc.invalidateQueries({ queryKey: ['api-credentials'] });
    onClose();
  };

  const copyKey = () => {
    if (!created) return;
    void navigator.clipboard
      ?.writeText(created.rawKey)
      .then(() => snackbar.show('Key copied.', 'success'))
      .catch(() => snackbar.show('Copy failed — clipboard unavailable.', 'error'));
  };

  if (created) {
    return (
      <AppDialog
        open={open}
        onClose={finish}
        size="sm"
        title={`Credential "${created.name}" created`}
        disableBackdropClose
        actions={
          <Button variant="contained" onClick={finish}>
            Done
          </Button>
        }
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            This key is shown <strong>once</strong> and cannot be retrieved again — it is stored
            hashed. Copy it to a safe place before closing.
          </Alert>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                component="code"
                sx={{
                  flex: 1,
                  fontFamily: 'monospace',
                  fontSize: 13,
                  wordBreak: 'break-all',
                  userSelect: revealed ? 'text' : 'none',
                }}
              >
                {revealed ? created.rawKey : '•'.repeat(48)}
              </Box>
              <Tooltip title={revealed ? 'Hide' : 'Reveal'}>
                <IconButton
                  size="small"
                  aria-label={revealed ? 'Hide key' : 'Reveal key'}
                  onClick={() => setRevealed((r) => !r)}
                >
                  {revealed ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy key">
                <IconButton size="small" aria-label="Copy key" onClick={copyKey}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            Hand the key to the integration partner over a secure channel. If it&apos;s ever lost
            or leaked, revoke this credential (#{created.id}) and mint a new one.
          </Typography>
        </Stack>
      </AppDialog>
    );
  }

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="New API credential"
      disableBackdropClose
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={mutation.isPending || !tenant} onClick={submit}>
            {mutation.isPending ? 'Creating…' : 'Create credential'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }} component="form" onSubmit={submit} noValidate>
        <TenantPicker
          value={tenant}
          featured={pageTenants}
          onChange={setTenant}
          label="Tenant"
          placeholder="Pick a tenant or System"
          extraOptions={[SYSTEM_TENANT]}
        />

        <TextField
          {...register('name')}
          size="small"
          label="Name"
          placeholder="e.g. Acme booking import"
          error={Boolean(errors.name)}
          helperText={errors.name?.message ?? 'Shown in grids and audit trails — name the consumer.'}
        />

        <Controller
          control={control}
          name="scope"
          render={({ field }) => (
            <TextField
              {...field}
              select
              size="small"
              label="Scope"
              error={Boolean(errors.scope)}
              helperText={
                errors.scope?.message ??
                'Which API surface accepts the key. Immutable after creation.'
              }
            >
              {SCOPE_CHOICES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {c.hint}
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <TextField
          {...register('description')}
          size="small"
          label="Description (optional)"
          multiline
          minRows={2}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label="Expires (optional, UTC)"
            value={expires}
            onChange={setExpires}
            slotProps={{
              textField: {
                size: 'small',
                helperText: 'Leave empty for a non-expiring key. Can be changed later.',
              },
            }}
          />
        </LocalizationProvider>
      </Stack>
    </AppDialog>
  );
}
